import { Card, Divider, Stack, Typography } from '@mui/joy';
import { ArrowRight } from '@phosphor-icons/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { IPartFinderComparison } from 'companySettings/types';
import { SlidingPanel } from 'core/components';
import { Loader } from 'core/elements';
import { formatDate } from 'core/utils';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

interface IChangeBoxProps {
  color: Color;
  children: ReactNode;
}

// One kind of change, tinted in its colour the way the app highlights a section card.
export const ChangeBox = ({ color, children }: IChangeBoxProps) => (
  <Card variant={Variant.OUTLINED} color={color} sx={{ backgroundColor: `${color}.softBg` }}>
    {children}
  </Card>
);

interface IGroupProps {
  title: string;
  color: Color;
  children: ReactNode;
}

// A tinted box of chips: what was added, removed or changed.
export const CompareGroup = ({ title, color, children }: IGroupProps) => (
  <ChangeBox color={color}>
    <Typography level={Level.TITLE_SM} color={color}>
      {title}
    </Typography>
    <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      {children}
    </Stack>
  </ChangeBox>
);

interface IProps {
  comparison?: IPartFinderComparison;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
}

// What changed between two published versions, older to newer: one outlined card with the
// versions, the name, then whatever the owner draws for its own content.
export const CompareView = ({
  comparison,
  isLoading,
  isError,
  errorMessage,
  isOpen,
  onClose,
  children,
}: IProps) => {
  const { t } = useTranslation();

  return (
    <SlidingPanel isOpen={isOpen} onClosePanel={onClose} title={t('titleCompareView')} level={1}>
      {isLoading && <Loader />}

      {isError && (
        <Typography level={Level.TITLE_SM} color={Color.DANGER}>
          {errorMessage}
        </Typography>
      )}

      {!isLoading && comparison && (
        <Card variant={Variant.OUTLINED} size={Size.MD}>
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1.5 }}>
            <Typography level={Level.TITLE_MD}>
              {t('labelVersionAndDate', {
                number: comparison.from.number,
                date: formatDate(comparison.from.versionedAt),
              })}
            </Typography>
            <ArrowRight />
            <Typography level={Level.TITLE_MD}>
              {t('labelVersionAndDate', {
                number: comparison.to.number,
                date: formatDate(comparison.to.versionedAt),
              })}
            </Typography>
          </Stack>

          <Divider inset="context" />

          <Stack sx={{ gap: 1 }}>
            <Typography level={Level.TITLE_MD}>{t('labelMetadata')}</Typography>
            {comparison.name ? (
              <ChangeBox color={Color.DANGER}>
                <Stack
                  direction={Direction.ROW}
                  sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
                >
                  <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                    {t('labelName')}
                  </Typography>
                  <Typography
                    level={Level.TITLE_SM}
                    color={Color.DANGER}
                    sx={{ textDecoration: 'line-through' }}
                  >
                    {comparison.name.from}
                  </Typography>
                  <ArrowRight />
                  <Typography level={Level.TITLE_SM} color={Color.SUCCESS}>
                    {comparison.name.to}
                  </Typography>
                </Stack>
              </ChangeBox>
            ) : (
              <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                {t('labelNoChanges')}
              </Typography>
            )}
          </Stack>

          <Divider inset="context" />

          {children}
        </Card>
      )}
    </SlidingPanel>
  );
};
